import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import en from "./locales/en.json";
import fr from "./locales/fr.json";

/**
 * Lightweight in-house i18n provider — no external dependency.
 * - Resolves dotted keys ("settings.title") against the active dictionary.
 * - Falls back to English, then to the key itself.
 * - Persists the selected locale in localStorage.
 *
 * Usage:
 *   const { t, locale, setLocale } = useI18n();
 *   <h1>{t("settings.title")}</h1>
 */

const DICTIONARIES = { en, fr };

export const SUPPORTED_LOCALES = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français (Beta)" },
  { code: "sw", label: "Kiswahili (Beta)" },
];

const STORAGE_KEY = "wise.locale";

const resolve = (dict, path) => {
  if (!dict) return undefined;
  return path.split(".").reduce((acc, key) => {
    if (acc && typeof acc === "object" && key in acc) return acc[key];
    return undefined;
  }, dict);
};

const interpolate = (str, vars) => {
  if (typeof str !== "string" || !vars) return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`));
};

const I18nContext = createContext(null);

const I18nProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "en";
    } catch {
      return "en";
    }
  });

  const setLocale = useCallback((next) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {}
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("lang", locale);
  }, [locale]);

  const t = useCallback(
    (key, vars) => {
      const active = DICTIONARIES[locale] || DICTIONARIES.en;
      const found = resolve(active, key) ?? resolve(DICTIONARIES.en, key);
      if (found === undefined) return key;
      return interpolate(found, vars);
    },
    [locale],
  );

  const value = useMemo(
    () => ({ t, locale, setLocale, supported: SUPPORTED_LOCALES }),
    [t, locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Render-safe fallback: return English without crashing if provider is missing.
    return {
      t: (k, v) => interpolate(resolve(DICTIONARIES.en, k) ?? k, v),
      locale: "en",
      setLocale: () => {},
      supported: SUPPORTED_LOCALES,
    };
  }
  return ctx;
};

export default I18nProvider;
